/**
 * Chips da instalação com inflamáveis: classe, estado do item do prontuário e situação. Toda
 * informação sai em palavra, nunca só pela cor. A validade (dias até vencer) é do `ValidityChip`.
 */
import React from 'react';
import { Chip, type SxProps, type Theme } from '@mui/material';
import {
  CheckCircle as OkIcon,
  ErrorOutline as BadIcon,
  HelpOutline as UnknownIcon,
  HourglassEmpty as PendingIcon,
  PowerOff as OffIcon,
  WarningAmber as WarnIcon,
} from '@mui/icons-material';
import {
  ROTULO_DA_CLASSE,
  ROTULO_DA_SITUACAO_DA_INSTALACAO,
  ROTULO_DO_ESTADO_DO_ITEM,
  type FlammableClass,
  type FlammableItemState,
  type FlammableSituation,
} from '../../../types/flammableInstallation';

type ToneColor = 'info' | 'warning' | 'success' | 'error' | 'neutral';

/**
 * Texto e borda no tom escuro da paleta (o `main` do aviso e do sucesso fica abaixo de 4,5:1 sobre o
 * branco) e letra de 14px. No tema escuro volta o tom claro, que é o que contrasta com o fundo escuro.
 */
const readableChipSx =
  (color: ToneColor): SxProps<Theme> =>
  (theme) => {
    if (color === 'neutral') {
      return { fontSize: '0.875rem', fontWeight: 500, '& .MuiChip-icon': { color: 'inherit' } };
    }
    return {
      fontSize: '0.875rem',
      fontWeight: 500,
      color: theme.palette[color].dark,
      borderColor: theme.palette[color].dark,
      '& .MuiChip-icon': { color: 'inherit' },
      ...theme.applyStyles('dark', {
        color: theme.palette[color].light,
        borderColor: theme.palette[color].light,
      }),
    };
  };

type ChipLook = { color: ToneColor; icon?: React.ReactElement };

const CLASS_CHIPS: Record<FlammableClass, ChipLook> = {
  I: { color: 'info' },
  II: { color: 'info' },
  III: { color: 'info' },
  ANEXO_II: { color: 'neutral' },
  ABAIXO_DO_LIMITE: { color: 'neutral' },
  SEM_DADOS: { color: 'neutral', icon: <UnknownIcon /> },
};

const ITEM_STATE_CHIPS: Record<FlammableItemState, ChipLook> = {
  EM_DIA: { color: 'success', icon: <OkIcon /> },
  VENCIDO: { color: 'error', icon: <BadIcon /> },
  AUSENTE: { color: 'warning', icon: <WarnIcon /> },
  NAO_EXIGIDO: { color: 'neutral' },
};

const SITUATION_CHIPS: Record<FlammableSituation, ChipLook> = {
  DESATIVADA: { color: 'neutral', icon: <OffIcon /> },
  ABAIXO_DO_LIMITE: { color: 'neutral' },
  EM_CADASTRO: { color: 'info', icon: <PendingIcon /> },
  EM_DIA: { color: 'success', icon: <OkIcon /> },
  ATENCAO: { color: 'warning', icon: <WarnIcon /> },
};

const LocalChip: React.FC<ChipLook & { label: string }> = ({ color, icon, label }) => (
  <Chip size="small" variant="outlined" icon={icon} label={label} sx={readableChipSx(color)} />
);

export const ClassChip: React.FC<{ classe: FlammableClass }> = ({ classe }) => (
  <LocalChip {...CLASS_CHIPS[classe]} label={ROTULO_DA_CLASSE[classe]} />
);

export const ItemStateChip: React.FC<{ state: FlammableItemState }> = ({ state }) => (
  <LocalChip {...ITEM_STATE_CHIPS[state]} label={ROTULO_DO_ESTADO_DO_ITEM[state]} />
);

export const SituationChip: React.FC<{ situation: FlammableSituation }> = ({ situation }) => (
  <LocalChip {...SITUATION_CHIPS[situation]} label={ROTULO_DA_SITUACAO_DA_INSTALACAO[situation]} />
);
